import { t } from "i18next";
import {
    baseButtonColor,
    CmpEventProductCalendar,
    formatAmount,
    GridDimensions,
    ILayoutInput,
    ProductSection,
    ProductButton,
    MultiProductButton,
    VenueAccessTicketButtonType,
} from "lib";
import React, { createContext, useEffect, useReducer, useRef } from "react";
import {
    LayoutReferenceData,
    ProductLayoutActionType,
    productLayoutReducer,
    ProductLayoutReducerState,
} from "./edit/product-layout-operation-reducer";
import {
    getLayoutProductIds,
    getMaxDimensionsForCoordinate,
} from "./functions";
import { LayoutActionType } from "./layout-operation-reducer";
import { useAdminSession } from "../../session";
import { useMerchantConfig } from "@venuepos/react-common";
import { AvailableLocale } from "locales";

export const LayoutButtonsContext =
    createContext<ProductLayoutReducerState | null>(null);

export const LayoutDispatchContext = createContext<React.Dispatch<
    LayoutActionType | ProductLayoutActionType
> | null>(null);

export function ProductLayoutProvider({
    sections,
    referenceData,
    gridDimensions,
    onLayoutUpdate,
    children,
}: {
    sections: ProductSection[];
    referenceData: LayoutReferenceData;
    gridDimensions: GridDimensions;
    onLayoutUpdate: (value: ILayoutInput["data"]) => void;
    children: React.ReactNode;
}) {
    const { currency } = useMerchantConfig();
    const [{ locale }] = useAdminSession(["locale"]);

    const [layoutState, dispatch] = useReducer(
        productLayoutReducer,
        {
            sections,
            referenceData,
            layoutProductIds: [],
            dimensions: gridDimensions,
            currentSectionIndex: 0,
            buttonMetaData: {},
            onLayoutUpdate,
            currency,
            locale: locale as AvailableLocale,
        },
        createInitialState
    );

    const firstSyncDone = useRef<boolean>(false);
    const onLayoutUpdateRef = useRef(onLayoutUpdate);

    useEffect(() => {
        onLayoutUpdateRef.current = onLayoutUpdate;
    }, [onLayoutUpdate]);

    useEffect(() => {
        if (!firstSyncDone.current) {
            firstSyncDone.current = true;
            return;
        }

        onLayoutUpdateRef.current(layoutState.sections);
    }, [layoutState.sections]);

    return (
        <LayoutButtonsContext.Provider value={layoutState}>
            <LayoutDispatchContext.Provider value={dispatch}>
                {children}
            </LayoutDispatchContext.Provider>
        </LayoutButtonsContext.Provider>
    );
}

function createInitialState({
    sections,
    referenceData,
    layoutProductIds,
    dimensions,
    currentSectionIndex,
    buttonMetaData,
    onLayoutUpdate,
    currency,
    locale,
}: ProductLayoutReducerState) {
    let returnValue: ProductLayoutReducerState = {
        sections,
        referenceData,
        layoutProductIds,
        dimensions,
        currentSectionIndex,
        buttonMetaData,
        onLayoutUpdate,
        currency,
        locale,
    };

    const formatAmountWithCurrency = (amount: number) =>
        formatAmount(amount, currency, { locale });

    const productsById = new Map(
        returnValue.referenceData.products.map(product => [product.id, product])
    );
    const productGroupsById = new Map(
        returnValue.referenceData.productGroups.map(group => [group.id, group])
    );
    const eventsByExternalId = new Map(
        returnValue.referenceData.events.map(event => [event.externalId, event])
    );
    const cmpProductsByExternalId = new Map(
        returnValue.referenceData.cmpProducts.map(product => [
            product.externalId,
            product,
        ])
    );
    const layoutEntitiesById = new Map(
        returnValue.referenceData.layoutEntities.map(entity => [
            entity.id,
            entity,
        ])
    );
    const venueAccessEventsById = new Map(
        returnValue.referenceData.venueAccessEvents.map(e => [e.id, e])
    );
    const venueAccessAccessTypesById = new Map(
        returnValue.referenceData.venueAccessAccessTypes.map(at => [at.id, at])
    );

    // Check the buttons of all sections for product and product group references, update button metadata array, etc.
    return {
        ...returnValue,
        layoutProductIds: getLayoutProductIds(returnValue.sections),

        sections: returnValue.sections.map(section => ({
            ...section,

            // loop the buttons of this section to find product and product group references
            buttons: section.buttons
                .map(button => {
                    if (!button) {
                        return;
                    }

                    // Set all basic properties for the button in the grid here.
                    returnValue.buttonMetaData[button.id] = {
                        maxHeightValue: 1,
                        maxWidthValue: 1,
                    };

                    if (button.buttonType === "PRODUCT") {
                        const buttonProduct = productsById.get(
                            (button as ProductButton).productId
                        );

                        if (!buttonProduct) {
                            return;
                        }

                        returnValue.buttonMetaData[button.id] = {
                            ...returnValue.buttonMetaData[button.id],
                            color:
                                button.color ||
                                buttonProduct.group.color ||
                                baseButtonColor,
                            text:
                                button.label ||
                                buttonProduct.buttonText ||
                                buttonProduct.name ||
                                t("backoffice.layout.no_text", "No text"),
                            formattedAmount: formatAmountWithCurrency(
                                buttonProduct.amount
                            ),
                        };
                    } else if (button.buttonType === "FUNCTION") {
                        returnValue.buttonMetaData[button.id] = {
                            ...returnValue.buttonMetaData[button.id],

                            text:
                                button.label ||
                                t("backoffice.layout.no_text", "No text"),
                        };

                        if (button.function === "CUSTOM_ITEM") {
                            const foundGroup = productGroupsById.get(
                                button.productGroupId
                            );

                            if (foundGroup) {
                                returnValue.buttonMetaData[button.id] = {
                                    ...returnValue.buttonMetaData[button.id],

                                    color:
                                        button.color ||
                                        foundGroup.color ||
                                        baseButtonColor,
                                    text: button.label,
                                    formattedAmount: null,
                                };
                            } else {
                                returnValue.buttonMetaData[button.id] = {
                                    ...returnValue.buttonMetaData[button.id],

                                    color: button.color || baseButtonColor,
                                    text: button.label,
                                    formattedAmount: null,
                                };
                            }
                        } else if (button.function === "VENUE_ACCESS_TICKETS") {
                            const venueAccessButton =
                                button as VenueAccessTicketButtonType;
                            const foundGroup = productGroupsById.get(
                                venueAccessButton.productGroupId
                            );
                            let missingConfigurationData = false;
                            let buttonText = venueAccessButton.label;

                            const foundEvent = venueAccessEventsById.get(
                                venueAccessButton.venueAccessEvent
                            );

                            if (!foundEvent) {
                                missingConfigurationData = true;

                                buttonText = t(
                                    "layout.error.missing_venue_access_event_data",
                                    "Event not found: {{ eventId }}",
                                    {
                                        eventId:
                                            venueAccessButton.venueAccessEvent,
                                    }
                                );
                            } else if (!foundEvent.isActive) {
                                missingConfigurationData = true;

                                buttonText = t(
                                    "layout.error.venue_access_event_inactive",
                                    "Event is not active: {{ eventId }}",
                                    {
                                        eventId:
                                            venueAccessButton.venueAccessEvent,
                                    }
                                );
                            } else {
                                const staleTypeId =
                                    venueAccessButton.venueAccessTypes?.find(
                                        id =>
                                            id != null &&
                                            !venueAccessAccessTypesById.get(id)
                                    );
                                if (staleTypeId != null) {
                                    missingConfigurationData = true;
                                    buttonText = t(
                                        "layout.error.missing_venue_access_type_data",
                                        "Ticket type not found: {{ id }}",
                                        { id: staleTypeId }
                                    );
                                }
                            }

                            returnValue.buttonMetaData[button.id] = {
                                ...returnValue.buttonMetaData[button.id],

                                color:
                                    venueAccessButton.color ||
                                    foundGroup?.color ||
                                    baseButtonColor,
                                text: buttonText,
                                formattedAmount:
                                    venueAccessButton.amount !== undefined
                                        ? formatAmountWithCurrency(
                                              venueAccessButton.amount
                                          )
                                        : null,
                                missingConfigurationData,
                            };
                        } else if (button.function === "BUY_ACCOUNT_FUNDS") {
                            returnValue.buttonMetaData[button.id] = {
                                ...returnValue.buttonMetaData[button.id],

                                color: button.color || baseButtonColor,
                                text: button.label,

                                formattedAmount: button.amount
                                    ? formatAmountWithCurrency(button.amount)
                                    : null,
                            };
                        } else if (button.function === "PLU_SEARCH") {
                            returnValue.buttonMetaData[button.id] = {
                                ...returnValue.buttonMetaData[button.id],

                                color: button.color || baseButtonColor,
                                text: button.label,

                                formattedAmount: null,
                            };
                        } else if (button.function === "LOGOUT") {
                            returnValue.buttonMetaData[button.id] = {
                                ...returnValue.buttonMetaData[button.id],

                                color: button.color || baseButtonColor,
                                text: button.label,

                                formattedAmount: null,
                            };
                        }
                    } else if (button.buttonType === "CMP_EVENT") {
                        let buttonLabel = "N/A";
                        let missingConfigurationData = false;

                        const foundEvent = eventsByExternalId.get(
                            button.eventId
                        );

                        if (foundEvent) {
                            buttonLabel = foundEvent.name;
                        } else {
                            missingConfigurationData = true;
                            buttonLabel = t(
                                "layout.error.missing_cmp_event_data",
                                "CMP Event not found: {{ cmpEventId }}",
                                { cmpEventId: button.eventId }
                            );
                        }

                        if (!missingConfigurationData && button.label) {
                            buttonLabel = button.label;
                        }

                        returnValue.buttonMetaData[button.id] = {
                            ...returnValue.buttonMetaData[button.id],

                            color: button.color,
                            text: buttonLabel,

                            missingConfigurationData,
                        };
                    } else if (button.buttonType === "CMP_EVENT_PRODUCT") {
                        let buttonLabel = "N/A";
                        let buttonAmount = "N/A";
                        let missingConfigurationData = false;

                        const foundEvent = eventsByExternalId.get(
                            button.eventId
                        );

                        let foundProduct;
                        if (foundEvent) {
                            buttonLabel = foundEvent.name;

                            foundProduct = foundEvent.products.find(
                                item => item.externalId === button.productId
                            );
                        } else {
                            buttonLabel = t(
                                "layout.error.missing_cmp_event_data",
                                "CMP Event not found: {{ cmpEventId }}",
                                { cmpEventId: button.eventId }
                            );
                            missingConfigurationData = true;
                        }

                        if (foundProduct) {
                            buttonLabel = foundProduct.name;

                            let calendar: CmpEventProductCalendar;
                            if (foundProduct.calendar) {
                                // There is a calendar, so just get the amount from the first date (or use 0 as fallback) and display it.
                                // It does not matter much if the date is right, we just want to show an amount here.

                                calendar = JSON.parse(foundProduct.calendar);

                                buttonAmount = formatAmountWithCurrency(
                                    calendar.dates[0]?.amount || 0
                                );
                                // }
                            } else if (foundProduct.amount) {
                                buttonAmount = formatAmountWithCurrency(
                                    foundProduct.amount
                                );
                            } else {
                                // The fallback if there's no amount in foundProduct - which should be unlikely.
                                buttonAmount = formatAmountWithCurrency(0);
                            }
                        } else {
                            // Only change the "error label" if we found an event, but the product is missing.
                            buttonLabel = !missingConfigurationData
                                ? t(
                                      "layout.error.missing_cmp_event_product_data",
                                      "CMP Event product not found: {{ cmpEventProductId }}",
                                      { cmpEventProductId: button.productId }
                                  )
                                : buttonLabel;
                            missingConfigurationData = true;
                        }

                        // If there were no missing data, then use the button label - if one is set.
                        if (!missingConfigurationData && button.label) {
                            buttonLabel = button.label;
                        }

                        returnValue.buttonMetaData[button.id] = {
                            ...returnValue.buttonMetaData[button.id],

                            color: button.color || baseButtonColor,
                            text: buttonLabel,

                            formattedAmount: buttonAmount,

                            // If the button is configured with missing CMP event/product data, then we present a GUI button with warnings.
                            missingConfigurationData,
                        };
                    } else if (button.buttonType === "MULTI_PRODUCT") {
                        let buttonLabel = button.label || "";
                        let buttonAmount = 0;
                        let missingConfigurationData = false;

                        const multiProductButton = button as MultiProductButton;

                        if (multiProductButton.productIds?.length) {
                            multiProductButton.productIds.forEach(item => {
                                const foundProduct = productsById.get(item.id);

                                if (!foundProduct) {
                                    missingConfigurationData = true;
                                    return;
                                }

                                buttonAmount +=
                                    foundProduct.amount * item.quantity;

                                if (!buttonLabel) {
                                    buttonLabel =
                                        foundProduct.buttonText ||
                                        foundProduct.name ||
                                        "";
                                }
                            });
                        }

                        if (multiProductButton.cmpEventProductIds?.length) {
                            multiProductButton.cmpEventProductIds.forEach(
                                item => {
                                    const foundEvent = eventsByExternalId.get(
                                        item.eventId
                                    );

                                    if (!foundEvent) {
                                        missingConfigurationData = true;
                                        return;
                                    }

                                    const foundProduct =
                                        foundEvent.products.find(
                                            productItem =>
                                                productItem.externalId ===
                                                item.productId
                                        );

                                    if (!foundProduct) {
                                        missingConfigurationData = true;
                                        return;
                                    }

                                    if (foundProduct.calendar) {
                                        const calendar = JSON.parse(
                                            foundProduct.calendar
                                        ) as CmpEventProductCalendar;

                                        buttonAmount +=
                                            (calendar.dates[0]?.amount || 0) *
                                            item.quantity;
                                    } else if (foundProduct.amount !== null) {
                                        buttonAmount +=
                                            foundProduct.amount * item.quantity;
                                    }

                                    if (!buttonLabel) {
                                        buttonLabel = foundProduct.name || "";
                                    }
                                }
                            );
                        }

                        if (!buttonLabel) {
                            buttonLabel = t(
                                "backoffice.layout.button_type_MULTI_PRODUCT",
                                "Multi product"
                            );
                        }

                        returnValue.buttonMetaData[button.id] = {
                            ...returnValue.buttonMetaData[button.id],
                            color: button.color || baseButtonColor,
                            text: buttonLabel,
                            formattedAmount:
                                formatAmountWithCurrency(buttonAmount),
                            missingConfigurationData,
                        };
                    } else if (button.buttonType === "CMP_PRODUCT") {
                        let buttonLabel = "N/A";
                        let buttonAmount = "N/A";
                        let missingConfigurationData = false;

                        const foundCmpProduct = cmpProductsByExternalId.get(
                            button.productId
                        );

                        if (foundCmpProduct) {
                            buttonLabel = foundCmpProduct.name;
                            if (foundCmpProduct.amount !== undefined) {
                                buttonAmount = formatAmountWithCurrency(
                                    foundCmpProduct.amount
                                );
                            }
                        } else {
                            missingConfigurationData = true;
                            buttonLabel = t(
                                "layout.error.missing_cmp_product_data",
                                "CMP Product not found: {{ cmpProductId }}",
                                { cmpProductId: button.productId }
                            );
                        }

                        if (!missingConfigurationData && button.label) {
                            buttonLabel = button.label;
                        }

                        returnValue.buttonMetaData[button.id] = {
                            ...returnValue.buttonMetaData[button.id],

                            color: button.color || baseButtonColor,
                            text: buttonLabel,

                            formattedAmount: buttonAmount,

                            // If the button is configured with missing CMP event/product data, then we present a GUI button with warnings.
                            missingConfigurationData,
                        };
                    } else if (button.buttonType === "LAYOUT_ENTITY") {
                        let buttonLabel = "N/A";
                        let missingConfigurationData = false;

                        const foundLayoutEntity = layoutEntitiesById.get(
                            button.entityId
                        );

                        if (foundLayoutEntity) {
                            buttonLabel = foundLayoutEntity.name;
                        } else {
                            missingConfigurationData = true;
                            buttonLabel = t(
                                "layout.error.missing_layout_entity_data",
                                "Layout entity not found: {{ layoutEntityId }}",
                                { layoutEntityId: button.entityId }
                            );
                        }

                        if (!missingConfigurationData && button.label) {
                            buttonLabel = button.label;
                        }

                        returnValue.buttonMetaData[button.id] = {
                            ...returnValue.buttonMetaData[button.id],

                            color: button.color,
                            text: buttonLabel,

                            missingConfigurationData,
                        };
                    } else {
                        // This is a button of unknown type. Back off. The button will not be shown in layout.
                        return;
                    }

                    // find the dimension bounds for the button
                    const { width: maxWidth, height: maxHeight } =
                        getMaxDimensionsForCoordinate(
                            {
                                x: button.x,
                                y: button.y,
                            },
                            dimensions,
                            returnValue.sections[
                                returnValue.currentSectionIndex
                            ]?.buttons,
                            []
                        );

                    returnValue.buttonMetaData[button.id] = {
                        ...returnValue.buttonMetaData[button.id],

                        maxWidthValue: maxWidth,
                        maxHeightValue: maxHeight,
                    };

                    return button;
                })
                // Remove buttons with a "dead" product reference
                .filter(Boolean),
        })),
    };
}
